import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, ExternalLink, ArrowRight, Clock, MapPin } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { seo } from "../translations/seo";
import { useLanguage } from "../context/LanguageContext";
import api from "../api/client";

export default function Staff() {
  const { t, language } = useLanguage();
  const location = useLocation();
  const [staff, setStaff] = useState([]);
  const [centresMap, setCentresMap] = useState({});

  useEffect(() => {
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/centres").then((r) => {
      const map = {};
      r.data.forEach((c) => { map[c.slug] = c; });
      setCentresMap(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("ring-2", "ring-[#3B9DA5]", "ring-offset-4");
        setTimeout(() => el.classList.remove("ring-2", "ring-[#3B9DA5]", "ring-offset-4"), 2400);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [location.hash, staff]);

  return (
    <div className="min-h-screen bg-white font-jakarta">
      <Navbar />
      <Seo title={seo[language].staff.title} description={seo[language].staff.description} />

      {/* Hero */}
      <div className="pt-28 pb-16 bg-gradient-to-br from-slate-50 to-blue-50/40" data-testid="staff-hero">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 text-center">
          <span className="text-[#3B9DA5] text-sm font-semibold uppercase tracking-widest">
            {t.staff.subtitle}
          </span>
          <h1 className="font-outfit text-5xl sm:text-6xl font-bold text-slate-900 mt-3">
            {t.staff.title}
          </h1>
        </div>
      </div>

      {/* Staff Grid */}
      <section className="py-16 pb-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="staff-grid">
            {staff.map((member) => (
              <div
                key={member.id}
                id={`staff-${member.id}`}
                className="bg-white rounded-3xl border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col overflow-hidden scroll-mt-24"
                data-testid={`staff-card-${member.id}`}
              >
                {/* Photo */}
                <div className="h-52 overflow-hidden bg-slate-100">
                  <img
                    src={member.photo}
                    alt={member.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Header */}
                <div className="p-6 pb-4">
                  <span className={`inline-block text-xs font-semibold uppercase tracking-wide px-3 py-1.5 rounded-full mb-4 ${member.color}`}>
                    {member.i18n?.[language]?.title || member.i18n?.fr?.title || member.specialty}
                  </span>
                  <h3 className="font-outfit font-bold text-slate-900 text-xl leading-snug">
                    {member.name}
                  </h3>
                  {centresMap[member.centre_slug || "cinquantenaire"] && (
                    <Link
                      to={`/centres/${member.centre_slug || "cinquantenaire"}`}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#2E4DB5] transition-colors group/centre"
                      data-testid={`staff-centre-${member.id}`}
                    >
                      <MapPin className="w-3.5 h-3.5 text-[#3B9DA5]" />
                      <span className="font-medium">{t.staff.practice_at}</span>
                      <span className="text-slate-700 group-hover/centre:text-[#2E4DB5] font-semibold">
                        {centresMap[member.centre_slug || "cinquantenaire"].name}
                      </span>
                    </Link>
                  )}
                </div>

                {/* Contact info */}
                <div className="px-6 space-y-2.5 flex-1">
                  <a
                    href={`tel:${member.phone.replace(/\s/g, "")}`}
                    className="flex items-center gap-3 text-sm text-slate-600 hover:text-[#2E4DB5] transition-colors group"
                    data-testid={`staff-phone-${member.id}`}
                  >
                    <span className="w-8 h-8 bg-slate-50 rounded-xl flex items-center justify-center group-hover:bg-blue-50 transition-colors flex-shrink-0">
                      <Phone className="w-4 h-4 text-[#3B9DA5]" />
                    </span>
                    {member.phone}
                  </a>
                  <a
                    href={`mailto:${member.email}`}
                    className="flex items-center gap-3 text-sm text-slate-600 hover:text-[#2E4DB5] transition-colors group"
                    data-testid={`staff-email-${member.id}`}
                  >
                    <span className="w-8 h-8 bg-slate-50 rounded-xl flex items-center justify-center group-hover:bg-blue-50 transition-colors flex-shrink-0">
                      <Mail className="w-4 h-4 text-[#3B9DA5]" />
                    </span>
                    <span className="truncate">{member.email}</span>
                  </a>
                </div>

                {/* Schedule */}
                <div className="mx-6 mt-4 bg-slate-50 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-[#3B9DA5]" />
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      {t.staff.schedule}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                    {member.schedule}
                  </p>
                </div>

                {/* CTA */}
                <div className="p-6 pt-4 flex gap-2">
                  <a
                    href={member.appointmentUrl}
                    target={member.appointmentUrl.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="flex-1 gradient-bg text-white text-sm font-semibold py-3 rounded-full flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                    data-testid={`staff-appointment-${member.id}`}
                  >
                    {member.appointmentUrl.startsWith("mailto:") ? t.staff.appointment : t.staff.appointment}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                  {member.has_detail && (
                    <Link
                      to={`/equipe/${member.slug}`}
                      className="px-4 py-3 text-sm font-semibold text-[#2E4DB5] border border-slate-200 rounded-full hover:bg-blue-50 hover:border-[#2E4DB5] transition-colors whitespace-nowrap"
                      data-testid={`staff-detail-link-${member.id}`}
                    >
                      {t.staff.learn_more}
                    </Link>
                  )}
                  {member.moreUrl && !member.has_detail && (
                    <a
                      href={member.moreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-11 h-11 flex items-center justify-center border border-slate-200 rounded-full hover:bg-slate-50 transition-colors"
                      data-testid={`staff-more-${member.id}`}
                      aria-label="External website"
                    >
                      <ExternalLink className="w-4 h-4 text-slate-400" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
